import { useState } from 'react';
import PropTypes from 'prop-types';

import { Input } from './index.styles';

/**
 * Renders a checkbox. Supports checked, unchecked and mixed states.
 */
export default function Checkbox({
  ariaLabel,
  checked,
  defaultChecked,
  id,
  name,
  onChange,
  onInput,
  onInvalid,
  value,
  size = 'regular',
  ...otherProps
}) {
  const [invalid, setInvalid] = useState(false);

  const props = {
    ...otherProps,
    checked,
    defaultChecked,
    'aria-label': ariaLabel,
  };

  if (onChange) {
    props.onChange = (event) => {
      const { target } = event;

      onChange(target.checked, event);
    };
  }

  return (
    <Input
      data-invalid={invalid}
      id={id}
      name={name}
      onInvalid={(event) => {
        if (onInvalid) {
          onInvalid(event);
        }
        setInvalid(true);
      }}
      onInput={(event) => {
        if (onInput) {
          onInput(event);
        }
        setInvalid(false);
      }}
      size={size}
      {...props}
    />
  );
}

Checkbox.propTypes = {
  /**
   * ARIA label.
   */
  ariaLabel: PropTypes.string,
  /**
   * Value. Use for controlled checkboxes along with `onChange`.
   */
  checked: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['mixed'])]),
  /**
   * Default checked. Use for uncontrolled inputs.
   */
  defaultChecked: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['mixed'])]),
  /**
   * Whether to disable the input.
   */
  disabled: PropTypes.bool,
  /**
   * Input ID. If not provided, ID will be automatically generated.
   */
  id: PropTypes.string,
  /**
   * Input name.
   */
  name: PropTypes.string.isRequired,
  /**
   * Function called on change. Will be called with two attributes: `checked` and `event`.
   */
  onChange: PropTypes.func,
  /**
   * Function called on input. Will be called with one attribute: `event`.
   */
  onInput: PropTypes.func,
  /**
   * Function called on invalid. Will be called with one attribute: `event`.
   */
  onInvalid: PropTypes.func,
  /**
   * Whether the input should be required.
   */
  required: PropTypes.bool,
  /**
   * Checkbox size.
   */
  size: PropTypes.oneOf(['regular', 'small']),
  /**
   * Checkbox value.
   */
  value: PropTypes.string,
};
